import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Camera, Languages, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useMyProfile } from "@/hooks/use-my-profile";
import {
  checkUsernameAvailability,
  updateMyProfile,
  validateUsernameFormat,
  normalizeUsername,
} from "@/lib/profile.functions";
import { resolvePublicSellerName } from "@/lib/public-name";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LanguageSelector } from "@/components/language-selector";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/settings/profile")({
  head: () => ({
    meta: [
      { title: "Account & Profile — BidNova Auctions" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Manage your BidNova account and public seller profile." },
    ],
  }),
  component: SettingsProfilePage,
});

const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);

function SettingsProfilePage() {
  const { user } = useAuth();
  const { t: tAccount } = useTranslation("account");
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useMyProfile();
  const check = useServerFn(checkUsernameAvailability);
  const save = useServerFn(updateMyProfile);

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [shopName, setShopName] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [socialTwitter, setSocialTwitter] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialTiktok, setSocialTiktok] = useState("");
  const [socialDiscord, setSocialDiscord] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.display_name ?? "");
    setUsername(profile.username ?? "");
    setShopName(profile.shop_name ?? "");
    setBio(profile.bio ?? "");
    setLocation(profile.location ?? "");
    setAvatarUrl(profile.avatar_url);
    const p = profile as unknown as {
      cover_image_url?: string | null;
      website_url?: string | null;
      social_links?: Record<string, string> | null;
    };
    setCoverUrl(p.cover_image_url ?? null);
    setWebsiteUrl(p.website_url ?? "");
    const s = p.social_links ?? {};
    setSocialTwitter(s.twitter ?? "");
    setSocialInstagram(s.instagram ?? "");
    setSocialFacebook(s.facebook ?? "");
    setSocialYoutube(s.youtube ?? "");
    setSocialTiktok(s.tiktok ?? "");
    setSocialDiscord(s.discord ?? "");
    setEnabled(profile.seller_profile_enabled);
  }, [profile]);


  const [availability, setAvailability] = useState<{ state: "idle" | "checking" | "ok" | "bad"; msg?: string }>({
    state: "idle",
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const norm = normalizeUsername(username);
    if (!norm) {
      setAvailability({ state: "idle" });
      return;
    }
    if (profile?.username && norm === profile.username) {
      setAvailability({ state: "ok", msg: "This is your current username." });
      return;
    }
    const fmt = validateUsernameFormat(norm);
    if (!fmt.ok) {
      setAvailability({ state: "bad", msg: fmt.error });
      return;
    }
    setAvailability({ state: "checking" });
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await check({ data: { username: norm } });
        if (res.available) setAvailability({ state: "ok", msg: "This username is available." });
        else setAvailability({ state: "bad", msg: res.reason });
      } catch {
        setAvailability({ state: "idle" });
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username, profile?.username, check]);

  const mutation = useMutation({
    mutationFn: async () => {
      return await save({
        data: {
          display_name: displayName.trim(),
          username: normalizeUsername(username),
          shop_name: shopName.trim() || null,
          bio: bio.trim() || null,
          location: location.trim() || null,
          avatar_url: avatarUrl,
          cover_image_url: coverUrl,
          website_url: websiteUrl.trim() || null,
          social_links: {
            twitter: socialTwitter.trim() || null,
            instagram: socialInstagram.trim() || null,
            facebook: socialFacebook.trim() || null,
            youtube: socialYoutube.trim() || null,
            tiktok: socialTiktok.trim() || null,
            discord: socialDiscord.trim() || null,
          },
          seller_profile_enabled: enabled,
        },
      });
    },
    onSuccess: () => {
      toast.success(t("profile.toastProfileUpdated", "Your public profile has been updated."));
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      queryClient.invalidateQueries({ queryKey: ["public-seller"] });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : t("profile.toastSaveFailed", "Could not save profile."));
    },
  });

  async function uploadImage(file: File, kind: "avatar" | "cover"): Promise<string | null> {
    if (!user) return null;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${user.id}/${kind}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, {
      upsert: true,
      contentType: file.type,
    });
    if (error) throw error;
    const { data: signed, error: signErr } = await supabase.storage
      .from("avatars")
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    if (signErr || !signed?.signedUrl) throw signErr ?? new Error("Could not sign image URL.");
    return signed.signedUrl;
  }

  async function handleCoverChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!ALLOWED_MIMES.has(file.type)) return toast.error(t("profile.toastInvalidImage", "Please choose a JPG, PNG, or WebP image."));
    if (file.size > AVATAR_MAX_BYTES) return toast.error(t("profile.toastImageTooLarge", "Image must be 5 MB or smaller."));
    setUploadingCover(true);
    try {
      const url = await uploadImage(file, "cover");
      if (url) {
        setCoverUrl(url);
        toast.success(t("profile.toastCoverReady", "Cover ready. Save your profile to publish it."));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("profile.toastUploadFailed", "Upload failed."));
    } finally {
      setUploadingCover(false);
    }
  }


  async function handleAvatarChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !user) return;
    if (!ALLOWED_MIMES.has(file.type)) {
      toast.error(t("profile.toastInvalidImage", "Please choose a JPG, PNG, or WebP image."));
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error(t("profile.toastImageTooLarge", "Image must be 5 MB or smaller."));
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, {
        upsert: true,
        contentType: file.type,
      });
      if (error) throw error;
      // Signed URL (bucket is private per workspace policy). 1-year expiry, refreshed on each edit.
      const { data: signed, error: signErr } = await supabase.storage
        .from("avatars")
        .createSignedUrl(path, 60 * 60 * 24 * 365);
      if (signErr || !signed?.signedUrl) throw signErr ?? new Error("Could not sign avatar URL.");
      setAvatarUrl(signed.signedUrl);
      toast.success(t("profile.toastAvatarReady", "Avatar ready. Save your profile to publish it."));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("profile.toastUploadFailed", "Upload failed."));
    } finally {
      setUploading(false);
    }
  }

  const previewName = useMemo(
    () => resolvePublicSellerName({ shop_name: shopName, display_name: displayName, username }),
    [shopName, displayName, username],
  );

  const canSave =
    displayName.trim().length > 0 &&
    normalizeUsername(username).length >= 3 &&
    availability.state !== "checking" &&
    availability.state !== "bad" &&
    !mutation.isPending;

  if (isLoading || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("profile.eyebrow", "Account")}</p>
        <h1 className="mt-2 font-display text-4xl">{t("profile.title", "Account & Profile")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("profile.subtitle", "Your login email remains private and is never shown on your public seller profile.")}
        </p>
      </header>

      {/* PRIVATE DETAILS */}
      <section aria-labelledby="private-heading" className="rounded-2xl border border-border/60 bg-card p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-gold" />
          <h2 id="private-heading" className="font-display text-xl">
            {t("profile.privateHeading", "Private account details")}
          </h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{t("profile.privateDesc", "Only visible to you. Never shown on public pages.")}</p>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t("profile.loginEmail", "Login email")}</dt>
            <dd className="mt-1 font-medium break-all">{user.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t("profile.emailVerified", "Email verified")}</dt>
            <dd className="mt-1 font-medium">{user.email_confirmed_at ? t("profile.verifiedYes", "Yes") : t("profile.verifiedNo", "Not yet verified")}</dd>
          </div>
          <div className="sm:col-span-2">
            <Link to="/auth/reset" className="text-sm text-gold underline">
              {t("profile.changePassword", "Change password")}
            </Link>
          </div>
        </dl>
      </section>

      {/* LANGUAGE */}
      <section
        aria-labelledby="language-heading"
        className="mt-6 rounded-2xl border border-border/60 bg-card p-6"
      >
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-gold" />
          <h2 id="language-heading" className="font-display text-xl">
            {tAccount("languageSection", "Language & region")}
          </h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {tAccount("preferredLanguageHelp", "Used across the site and for the emails we send you.")}
        </p>
        <div className="mt-4">
          <LanguageSelector />
        </div>
      </section>



      {/* PUBLIC PROFILE */}
      <form
        className="mt-6 rounded-2xl border border-border/60 bg-card p-6 gold-ring space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          mutation.mutate();
        }}
        aria-labelledby="public-heading"
      >
        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-gold" />
          <h2 id="public-heading" className="font-display text-xl">
            {t("profile.publicHeading", "Public profile")}
          </h2>
        </div>

        <div className="flex items-start gap-4">
          <Avatar className="h-20 w-20">
            {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
            <AvatarFallback className="text-lg">{(previewName[0] ?? "?").toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Label htmlFor="avatar-input" className="block">
              {t("profile.avatarLabel", "Avatar or shop logo")}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">{t("profile.avatarHelp", "JPG, PNG, or WebP · up to 5 MB.")}</p>
            <div className="mt-2 flex gap-2">
              <label
                htmlFor="avatar-input"
                className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm cursor-pointer hover:border-gold"
              >
                <Camera className="h-4 w-4" />
                {uploading ? t("profile.uploading", "Uploading…") : t("profile.uploadImage", "Upload image")}
              </label>
              <input
                id="avatar-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={handleAvatarChange}
                disabled={uploading}
              />
              {avatarUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setAvatarUrl(null)}>
                  {t("profile.remove", "Remove")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="display_name">
              {t("profile.displayName", "Display name")} <span aria-hidden className="text-destructive">*</span>
            </Label>
            <Input
              id="display_name"
              required
              maxLength={80}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="nickname"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t("profile.displayNameHelp", "Your public personal or community name.")}</p>
          </div>
          <div>
            <Label htmlFor="username">
              {t("profile.username", "Username")} <span aria-hidden className="text-destructive">*</span>
            </Label>
            <Input
              id="username"
              required
              minLength={3}
              maxLength={30}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              aria-describedby="username-help username-status"
            />
            <p id="username-help" className="mt-1 text-xs text-muted-foreground">
              {t("profile.usernameHelp", "3–30 chars, lowercase letters, numbers, hyphens. Used in your public URL:")}{" "}
              <span className="font-mono">/seller/{normalizeUsername(username) || "your-name"}</span>
            </p>
            <p
              id="username-status"
              aria-live="polite"
              role="status"
              className={`mt-1 text-xs ${
                availability.state === "ok"
                  ? "text-emerald-400"
                  : availability.state === "bad"
                    ? "text-destructive"
                    : "text-muted-foreground"
              }`}
            >
              {availability.state === "checking" ? t("profile.checkingAvailability", "Checking availability…") : availability.msg ?? ""}
            </p>
            {profile?.username_changed_at && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("profile.usernameChangeNote", "Username can be changed once every 30 days.")}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="shop_name">{t("profile.shopName", "Shop name")}</Label>
            <Input
              id="shop_name"
              maxLength={80}
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder={t("profile.shopNamePlaceholder", "Optional — e.g. Double Blessings TCG")}
            />
            <p className="mt-1 text-xs text-muted-foreground">{t("profile.shopNameHelp", "Shown to buyers instead of your display name when set.")}</p>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="bio">{t("profile.bio", "Bio")}</Label>
            <Textarea
              id="bio"
              maxLength={500}
              rows={4}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t("profile.bioPlaceholder", "A short public introduction. Plain text only.")}
            />
            <p className="mt-1 text-xs text-muted-foreground">{bio.length}/500</p>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="location">{t("profile.location", "Public location")}</Label>
            <Input
              id="location"
              maxLength={80}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t("profile.locationPlaceholder", "e.g. Manchester, United Kingdom")}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("profile.locationHelp", "Do not enter your full home address. This information may be visible publicly.")}
            </p>
          </div>

          <div className="sm:col-span-2">
            <Label>{t("profile.coverImage", "Cover image")}</Label>
            <p className="text-xs text-muted-foreground mt-1">{t("profile.coverImageHelp", "Wide banner shown at the top of your public seller page. JPG, PNG, or WebP · up to 5 MB.")}</p>
            <div
              className="mt-2 relative h-32 w-full rounded-lg border border-border/60 overflow-hidden bg-gradient-to-br from-primary/20 via-black/30 to-gold/15"
              style={coverUrl ? { backgroundImage: `url(${coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
            >
              {!coverUrl && <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">{t("profile.noCoverImage", "No cover image yet")}</div>}
            </div>
            <div className="mt-2 flex gap-2">
              <label htmlFor="cover-input" className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm cursor-pointer hover:border-gold">
                <Camera className="h-4 w-4" />
                {uploadingCover ? t("profile.uploading", "Uploading…") : coverUrl ? t("profile.replaceCover", "Replace cover") : t("profile.uploadCover", "Upload cover")}
              </label>
              <input id="cover-input" type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleCoverChange} disabled={uploadingCover} />
              {coverUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setCoverUrl(null)}>{t("profile.remove", "Remove")}</Button>
              )}
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="website_url">{t("profile.website", "Website")}</Label>
            <Input id="website_url" type="url" maxLength={500} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://your-shop.example" />
          </div>

          <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
            <div><Label htmlFor="s_twitter">{t("profile.socialTwitter", "Twitter / X")}</Label><Input id="s_twitter" value={socialTwitter} onChange={(e) => setSocialTwitter(e.target.value)} placeholder="https://twitter.com/…" /></div>
            <div><Label htmlFor="s_instagram">{t("profile.socialInstagram", "Instagram")}</Label><Input id="s_instagram" value={socialInstagram} onChange={(e) => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/…" /></div>
            <div><Label htmlFor="s_facebook">{t("profile.socialFacebook", "Facebook")}</Label><Input id="s_facebook" value={socialFacebook} onChange={(e) => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/…" /></div>
            <div><Label htmlFor="s_youtube">{t("profile.socialYoutube", "YouTube")}</Label><Input id="s_youtube" value={socialYoutube} onChange={(e) => setSocialYoutube(e.target.value)} placeholder="https://youtube.com/@…" /></div>
            <div><Label htmlFor="s_tiktok">{t("profile.socialTiktok", "TikTok")}</Label><Input id="s_tiktok" value={socialTiktok} onChange={(e) => setSocialTiktok(e.target.value)} placeholder="https://tiktok.com/@…" /></div>
            <div><Label htmlFor="s_discord">{t("profile.socialDiscord", "Discord")}</Label><Input id="s_discord" value={socialDiscord} onChange={(e) => setSocialDiscord(e.target.value)} placeholder="https://discord.gg/…" /></div>
          </div>
        </div>



        <div className="flex items-center justify-between rounded-lg border border-border/60 p-4">
          <div>
            <Label htmlFor="enabled" className="font-medium">
              {t("profile.publicSellerProfile", "Public seller profile")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("profile.publicSellerProfileDesc", "When on, buyers can visit your seller page at")}{" "}
              <span className="font-mono">/seller/{normalizeUsername(username) || "your-name"}</span>.
            </p>
          </div>
          <Switch id="enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {/* Live preview */}
        <div className="rounded-lg border border-gold/30 bg-gold/5 p-4">
          <p className="text-xs uppercase tracking-widest text-gold">{t("profile.publicPreview", "Public preview")}</p>
          <div className="mt-2 flex items-center gap-3">
            <Avatar className="h-12 w-12">
              {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
              <AvatarFallback>{(previewName[0] ?? "?").toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-medium truncate">{previewName}</p>
              {normalizeUsername(username) && (
                <p className="text-xs text-muted-foreground truncate">
                  @{normalizeUsername(username)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button type="submit" disabled={!canSave}>
            {mutation.isPending ? t("profile.saving", "Saving…") : t("profile.saveProfile", "Save profile")}
          </Button>
        </div>
      </form>
    </div>
  );
}
